// src/pages/AdminDashboard.jsx
import React, { useEffect, useState, useMemo } from "react";
import Swal from "sweetalert2";
import API from "../api/api";
import "./AdminDashboard.css";
import statesData from "../data/statesData";

/* ============================================================
   MULTI-SELECT DROPDOWN (STATE SELECTOR)
============================================================ */
function StatesSelector({ value, onChange }) {
  const allStates = useMemo(
    () => ["All India", ...statesData.map((s) => s.name)],
    []
  );

  const [selected, setSelected] = useState(value || ["All India"]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setSelected(value && value.length ? value : ["All India"]);
  }, [value]);

  const toggleState = (state) => {
    if (state === "All India") {
      setSelected(["All India"]);
      onChange(["All India"]);
      return;
    }

    let updated = selected.includes("All India") ? [] : [...selected];

    if (updated.includes(state)) {
      updated = updated.filter((s) => s !== state);
    } else {
      updated.push(state);
    }

    if (updated.length === 0) updated = ["All India"];

    setSelected(updated);
    onChange(updated);
  };

  const filtered = allStates.filter((s) =>
    s.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="mb-3 position-relative">
      <label className="form-label fw-semibold">Applicable States</label>

      <div
        className="form-control d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen(!open)}
      >
        <span className="text-muted">
          {selected.length > 3
            ? `${selected.length} states selected`
            : selected.join(", ")}
        </span>
        <i className={`bi bi-chevron-${open ? "up" : "down"}`}></i>
      </div>

      {open && (
        <div
          className="dropdown-menu show w-100 p-2 mt-1 shadow-sm"
          style={{ maxHeight: 300, overflowY: "auto" }}
        >
          <input
            type="text"
            className="form-control mb-2"
            placeholder="Search state..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {filtered.map((state) => (
            <label
              key={state}
              className="dropdown-item d-flex align-items-center"
            >
              <input
                type="checkbox"
                className="form-check-input me-2"
                checked={selected.includes(state)}
                onChange={() => toggleState(state)}
              />
              {state}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   MAIN ADMIN DASHBOARD
============================================================ */
export default function AdminDashboard() {
  const [schemes, setSchemes] = useState([]);

  const [mainCategory, setMainCategory] = useState("");

  const [categories, setCategories] = useState([
    {
      name: "",
      subcategories: [
        {
          name: "",
          applyLink: "",
          youtubeLink: "",
        },
      ],
    },
  ]);

  const [editingId, setEditingId] = useState(null);
  const [selectedStates, setSelectedStates] = useState(["All India"]);

  /* -----------------------------------------
     LOAD ALL SCHEMES
  ----------------------------------------- */
  const fetchSchemes = async () => {
    try {
      const res = await API.get("/schemes");
      setSchemes(res.data);
    } catch (err) {
      console.error("Error loading schemes:", err);
    }
  };

  /* -----------------------------------------
     LOAD EDIT DATA FROM LOCALSTORAGE
  ----------------------------------------- */
  useEffect(() => {
    const stored = localStorage.getItem("edit_scheme");
    if (stored) {
      const scheme = JSON.parse(stored);

      setEditingId(scheme._id);
      setMainCategory(scheme.mainCategory || "");

      // No visibility here!
      setCategories(
        scheme.categories.map((c) => ({
          name: c.name,
          subcategories: c.subcategories.map((sc) => ({
            name: sc.name,
            applyLink: sc.applyLink || "",
            youtubeLink: sc.youtubeLink || "",
          })),
        }))
      );

      setSelectedStates(
        Array.isArray(scheme.states) && scheme.states.length
          ? scheme.states
          : ["All India"]
      );

      localStorage.removeItem("edit_scheme");
    }

    fetchSchemes();
  }, []);

  /* -----------------------------------------
     CATEGORY HANDLERS
  ----------------------------------------- */
  const handleCategoryChange = (i, field, value) => {
    const updated = [...categories];
    updated[i][field] = value;
    setCategories(updated);
  };

  const handleSubChange = (ci, si, field, value) => {
    const updated = [...categories];
    updated[ci].subcategories[si][field] = value;
    setCategories(updated);
  };

  const addCategory = () =>
    setCategories([
      ...categories,
      {
        name: "",
        subcategories: [
          {
            name: "",
            applyLink: "",
            youtubeLink: "",
          },
        ],
      },
    ]);

  const deleteCategory = (i) =>
    setCategories(categories.filter((_, idx) => idx !== i));

  const addSubcategory = (ci) => {
    const updated = [...categories];
    updated[ci].subcategories.push({
      name: "",
      applyLink: "",
      youtubeLink: "",
    });
    setCategories(updated);
  };

  const deleteSubcategory = (ci, si) => {
    const updated = [...categories];
    updated[ci].subcategories.splice(si, 1);
    setCategories(updated);
  };

  /* -----------------------------------------
     SUBMIT FORM
  ----------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      mainCategory,
      categories,
      states: selectedStates,
    };

    const confirm = await Swal.fire({
      title: editingId ? "Update Scheme?" : "Create New Scheme?",
      icon: "question",
      showCancelButton: true,
    });

    if (!confirm.isConfirmed) return;

    try {
      if (editingId) {
        await API.put(`/schemes/${editingId}`, payload);
      } else {
        await API.post("/schemes", payload);
      }

      Swal.fire("Saved", "Scheme saved successfully!", "success");

      resetForm();
      fetchSchemes();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to save scheme", "error");
    }
  };

  const resetForm = () => {
    setMainCategory("");

    setCategories([
      {
        name: "",
        subcategories: [
          {
            name: "",
            applyLink: "",
            youtubeLink: "",
          },
        ],
      },
    ]);

    setSelectedStates(["All India"]);
    setEditingId(null);
  };

  /* -----------------------------------------
     UI
  ----------------------------------------- */
  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-card shadow-lg">
        <h3 className="dashboard-title mb-3">
          {editingId ? "Edit Card" : "Add New Card"}
        </h3>

        <form onSubmit={handleSubmit}>
          {/* MAIN CATEGORY */}
          <input
            className="form-control input-box mb-3"
            placeholder="Service Group
"
            value={mainCategory}
            onChange={(e) => setMainCategory(e.target.value)}
            required
          />

          {/* STATE SELECTOR */}
          <StatesSelector
            value={selectedStates}
            onChange={(arr) => setSelectedStates(arr)}
          />

          {/* CATEGORY UI */}
          {categories.map((cat, ci) => (
            <div key={ci} className="category-box mb-4">
              <div className="d-flex justify-content-between mb-2">
                <h6 className="fw-bold text-primary">Service Type {ci + 1}</h6>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => deleteCategory(ci)}
                >
                  Delete Service Type

                </button>
              </div>

              {/* CATEGORY NAME */}
              <input
                className="form-control mb-2"
                placeholder="Service Type"
                value={cat.name}
                onChange={(e) =>
                  handleCategoryChange(ci, "name", e.target.value)
                }
                required
              />

              {/* SUBCATEGORIES */}
              {cat.subcategories.map((sub, si) => (
                <div key={si} className="subcategory-box mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-secondary">
                      {ci + 1}.{si + 1}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteSubcategory(ci, si)}
                    >
                      Remove
                    </button>
                  </div>

                  {/* Subcategory Name */}
                  <input
                    className="form-control mb-1"
                    placeholder="Service Action"
                    value={sub.name}
                    onChange={(e) =>
                      handleSubChange(ci, si, "name", e.target.value)
                    }
                    required
                  />

                  {/* Apply Link */}
                  <input
                    className="form-control mb-1"
                    placeholder="Apply Link"
                    value={sub.applyLink}
                    onChange={(e) =>
                      handleSubChange(ci, si, "applyLink", e.target.value)
                    }
                  />

                  {/* YouTube Link */}
                  <input
                    className="form-control mb-1"
                    placeholder="YouTube Link"
                    value={sub.youtubeLink}
                    onChange={(e) =>
                      handleSubChange(ci, si, "youtubeLink", e.target.value)
                    }
                  />
                </div>
              ))}

              {/* Add Subcategory Button */}
              <button
                type="button"
                className="btn btn-sm btn-outline-success"
                onClick={() => addSubcategory(ci)}
              >
                + Add Service Action

              </button>
            </div>
          ))}

          {/* Add Category Button */}
          <button
            type="button"
            className="btn btn-outline-primary mb-3"
            onClick={addCategory}
          >
            + Add Service Type

          </button>

          {/* Submit */}
          <div className="text-end">
            <button type="submit" className="btn btn-success me-2">
              {editingId ? "Update" : "Create Card"}
            </button>

            {editingId && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetForm}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
